import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowLeft, Search, ShoppingCart, RefreshCw, AlertCircle,
  CheckCircle, ChevronDown, ChevronUp, PackageCheck, Clock,
  XCircle, Truck, Save, X, Send, ThumbsUp, FileEdit, Trash2,
  Lock, ShieldCheck, Plus, Building, Package
} from "lucide-react";
import { apiFetch } from "../utils/api";

/**
 * Purchase Orders tab of the Purchasing hub — lifecycle UI over
 * /api/inventory/purchase-orders.
 *
 * A PO moves draft → submitted → approved → sent → (partial) → received
 * (or cancelled); STATUS_META is the visual legend for that state
 * machine. Which transitions are allowed is enforced server-side
 * (routes/inventory.py) — this page renders the actions the backend
 * reports, plus a Lock/approval affordance.
 * InventorySearchInput debounces (280ms) a server search so PO lines can
 * be picked from the item catalogue with the supplier's own pack/price
 * rows.
 */

// ── Helpers ───────────────────────────────────────────────────────────────────

var STATUS_META = {
  draft:     { label: "Draft",     cls: "bg-gray-100 text-gray-600",    icon: <FileEdit className="h-3 w-3" /> },
  submitted: { label: "Submitted", cls: "bg-amber-100 text-amber-700",  icon: <Clock className="h-3 w-3" /> },
  approved:  { label: "Approved",  cls: "bg-blue-100 text-blue-700",    icon: <ThumbsUp className="h-3 w-3" /> },
  sent:      { label: "Sent",      cls: "bg-purple-100 text-purple-700",icon: <Send className="h-3 w-3" /> },
  partial:   { label: "Partial",   cls: "bg-cyan-100 text-cyan-700",    icon: <Truck className="h-3 w-3" /> },
  received:  { label: "Received",  cls: "bg-green-100 text-green-700",  icon: <PackageCheck className="h-3 w-3" /> },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-500",      icon: <XCircle className="h-3 w-3" /> },
  pending:   { label: "Pending",   cls: "bg-amber-100 text-amber-700",  icon: <Clock className="h-3 w-3" /> },
};

function fmtDate(iso) {
  if (!iso) return "";
  return iso.replace("T", " ").slice(0, 16);
}

// ── Inventory search input ──────────────────────────────────────────────

const InventorySearchInput = ({ value, onChange, onSelect, placeholder, inputClass }) => {
  var timerRef = useRef(null);
  var inputRef = useRef(null);
  var [results, setResults] = useState([]);
  var [open, setOpen] = useState(false);
  var [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 200 });
  var cls = inputClass || "w-full border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none";

  var calcPos = function() {
    if (!inputRef.current) return;
    var r = inputRef.current.getBoundingClientRect();
    setDropPos({ top: r.bottom + 2, left: r.left, width: r.width });
  };

  var handleChange = function(e) {
    var v = e.target.value;
    onChange(v);
    clearTimeout(timerRef.current);
    if (v.length < 2) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(function() {
      apiFetch("/api/inventory/items?search=" + encodeURIComponent(v) + "&limit=15")
        .then(function(r) { return r.json(); })
        .then(function(d) {
          var items = d.items || [];
          if (items.length > 0) calcPos();
          setResults(items);
          setOpen(items.length > 0);
        })
        .catch(function() {});
    }, 280);
  };

  var handleSelect = function(item) {
    onSelect(item);
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input ref={inputRef} value={value} onChange={handleChange} placeholder={placeholder}
        className={cls}
        onBlur={function() { setTimeout(function() { setOpen(false); }, 150); }} />
      {open && results.length > 0 && (
        <div style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl max-h-52 overflow-auto">
          {results.map(function(item) {
            return (
              <button type="button" key={item.id}
                onClick={function() { handleSelect(item); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 border-b border-gray-50 last:border-0">
                <div className="font-medium text-gray-800 truncate flex items-center gap-1">
                  <Package className="h-3 w-3 text-gray-400 shrink-0" />{item.description}
                </div>
                {(item.generic_name || item.product_code) && (
                  <div className="text-gray-400 truncate pl-4">{[item.generic_name, item.product_code].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Create PO Modal ──────────────────────────────────────────────────────────

const CreatePOModal = ({ onClose, onCreated }) => {
  var blankLine = function() {
    return { description: "", inventory_item_id: "", supplier_code: "", nominal_code: "", qty_ordered: "1", unit_price: "" };
  };
  var [supplierSearch, setSupplierSearch] = useState("");
  var [supplierResults, setSupplierResults] = useState([]);
  var [supplier, setSupplier] = useState(null); // { id, name }
  var [notes, setNotes] = useState("");
  var [lines, setLines] = useState([blankLine()]);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);
  var [searching, setSearching] = useState(false);
  var [hub, setHub] = useState(null); // { id, name }
  var [hubs, setHubs] = useState([]);

  var inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none";

  // Debounced supplier search
  useEffect(function() {
    if (supplierSearch.length < 2) { setSupplierResults([]); return; }
    setSearching(true);
    var t = setTimeout(function() {
      apiFetch("/api/purchasing/suppliers?q=" + encodeURIComponent(supplierSearch) + "&limit=10")
        .then(function(r) { return r.json(); })
        .then(function(d) { setSupplierResults(d.items || []); })
        .catch(function() {})
        .finally(function() { setSearching(false); });
    }, 280);
    return function() { clearTimeout(t); };
  }, [supplierSearch]);

  // Fetch hubs on mount
  useEffect(function() {
    apiFetch("/api/purchasing/hubs")
      .then(function(r) { return r.json(); })
      .then(function(d) { setHubs(d.items || []); })
      .catch(function() {});
  }, []);

  var setLine = function(i, field, val) {
    setLines(function(prev) {
      var next = prev.slice();
      next[i] = Object.assign({}, next[i], { [field]: val });
      return next;
    });
  };

  var addLine = function() { setLines(function(prev) { return prev.concat(blankLine()); }); };

  var removeLine = function(i) {
    setLines(function(prev) { return prev.filter(function(_, idx) { return idx !== i; }); });
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!supplier) { setError("Select a supplier."); return; }
    var validLines = lines.filter(function(l) { return l.description.trim(); });
    if (validLines.length === 0) { setError("Add at least one line with a description."); return; }
    setSaving(true); setError(null);
    try {
      var payload = {
        supplier_id: supplier.id || "",
        supplier_name: supplier.name,
        hub_id: hub ? hub.id : "",
        hub_name: hub ? hub.name : "",
        notes: notes,
        lines: validLines.map(function(l) {
          return {
            inventory_item_id: l.inventory_item_id || "",
            inventory_item_description: l.description.trim(),
            supplier_code: l.supplier_code.trim(),
            nominal_code: l.nominal_code.trim(),
            qty_ordered: parseFloat(l.qty_ordered) || 1,
            unit_price: l.unit_price.trim(),
            supplier_id: supplier.id || "",
            supplier_name: supplier.name,
            qty_received: 0,
            received: false,
            received_at: ""
          };
        })
      };
      var res = await apiFetch("/api/inventory/purchase-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Create failed");
      onCreated(data);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={function(e) { e.stopPropagation(); }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-green-600" /> New Purchase Order
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-auto flex-1 px-6 py-4 space-y-5">

            {/* Supplier picker */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">Supplier <span className="text-red-500">*</span></label>
              {supplier ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <Building className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-green-800 flex-1">{supplier.name}</span>
                  <button type="button" onClick={function() { setSupplier(null); setSupplierSearch(""); }}
                    className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input value={supplierSearch} onChange={function(e) { setSupplierSearch(e.target.value); }}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
                    placeholder="Search registered suppliers…" autoFocus />
                  {searching && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />}
                  {supplierResults.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {supplierResults.map(function(s) {
                        return (
                          <button type="button" key={s.id}
                            onClick={function() { setSupplier({ id: s.id, name: s.name }); setSupplierSearch(""); setSupplierResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 flex items-center gap-2">
                            <Building className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span className="font-medium text-gray-800">{s.name}</span>
                            {s.postcode && <span className="text-xs text-gray-400 ml-1">{s.postcode}</span>}
                          </button>
                        );
                      })}
                      {/* Allow typing a new supplier name not in the system */}
                      <button type="button"
                        onClick={function() { setSupplier({ id: "", name: supplierSearch.trim() }); setSupplierSearch(""); setSupplierResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 border-t border-gray-100 flex items-center gap-2">
                        <Plus className="h-3.5 w-3.5" /> Use &ldquo;{supplierSearch}&rdquo; as supplier name
                      </button>
                    </div>
                  )}
                  {supplierSearch.length >= 2 && !searching && supplierResults.length === 0 && (
                    <button type="button"
                      onClick={function() { setSupplier({ id: "", name: supplierSearch.trim() }); setSupplierSearch(""); }}
                      className="mt-1 text-xs text-purple-600 hover:underline flex items-center gap-1">
                      <Plus className="h-3 w-3" /> Use &ldquo;{supplierSearch}&rdquo; as supplier name
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-600">Notes</label>
              <input value={notes} onChange={function(e) { setNotes(e.target.value); }}
                className={inp} placeholder="Optional PO notes…" />
            </div>

            {/* Hub */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">Hub <span className="text-red-500">*</span></label>
              <select value={hub ? hub.id : ""}
                onChange={function(e) {
                  var sel = hubs.find(function(h) { return h.id === e.target.value; });
                  setHub(sel ? { id: sel.id, name: sel.name } : null);
                }}
                className={inp + " bg-white"}>
                <option value="">— Select hub —</option>
                {hubs.map(function(h) { return <option key={h.id} value={h.id}>{h.name}</option>; })}
              </select>
              {hub && <p className="text-xs text-green-600 flex items-center gap-1">✓ All lines will be assigned to {hub.name}</p>}
            </div>

            {/* Lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">Order Lines</label>
                <button type="button" onClick={addLine}
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800">
                  <Plus className="h-3.5 w-3.5" /> Add line
                </button>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-12 gap-2 px-2">
                <div className="col-span-4 text-xs text-gray-400 font-medium">Description *</div>
                <div className="col-span-2 text-xs text-gray-400 font-medium">Supplier Code</div>
                <div className="col-span-2 text-xs text-gray-400 font-medium">Nominal Code</div>
                <div className="col-span-1 text-xs text-gray-400 font-medium">Qty</div>
                <div className="col-span-2 text-xs text-gray-400 font-medium">Unit Price</div>
                <div className="col-span-1"></div>
              </div>

              <div className="space-y-1.5">
                {lines.map(function(line, i) {
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <InventorySearchInput
                          value={line.description}
                          onChange={function(v) { setLine(i, "description", v); }}
                          onSelect={function(item) {
                            var defSup = (item.suppliers || []).find(function(s) { return s.is_default; }) || (item.suppliers || [])[0] || {};
                            setLines(function(prev) {
                              var next = prev.slice();
                              next[i] = Object.assign({}, next[i], {
                                description: item.description || "",
                                inventory_item_id: item.id || "",
                                supplier_code: defSup.supplier_code || next[i].supplier_code,
                                nominal_code: item.nominal_code || next[i].nominal_code,
                              });
                              return next;
                            });
                          }}
                          inputClass={inp + " text-xs py-1.5"}
                          placeholder="Search inventory or type description…"
                        />
                      </div>
                      <div className="col-span-2">
                        <input value={line.supplier_code}
                          onChange={function(e) { setLine(i, "supplier_code", e.target.value); }}
                          className={inp + " text-xs py-1.5 font-mono"}
                          placeholder="SUP-001" />
                      </div>
                      <div className="col-span-2">
                        <input value={line.nominal_code}
                          onChange={function(e) { setLine(i, "nominal_code", e.target.value); }}
                          className={inp + " text-xs py-1.5"}
                          placeholder="5000" />
                      </div>
                      <div className="col-span-1">
                        <input type="number" min="0" step="any" value={line.qty_ordered}
                          onChange={function(e) { setLine(i, "qty_ordered", e.target.value); }}
                          className={inp + " text-xs py-1.5 text-right"} />
                      </div>
                      <div className="col-span-2">
                        <input type="number" min="0" step="0.0001" value={line.unit_price}
                          onChange={function(e) { setLine(i, "unit_price", e.target.value); }}
                          className={inp + " text-xs py-1.5 text-right"}
                          placeholder="0.00" />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {lines.length > 1 && (
                          <button type="button" onClick={function() { removeLine(i); }}
                            className="text-gray-300 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {error && (
            <div className="mx-6 mb-2 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
              {saving ? "Creating…" : "Create Draft PO"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Receive Lines Modal ───────────────────────────────────────────────────────

const ReceiveModal = ({ po, onClose, onReceived }) => {
  // Build local state: for each unreceived line, track qty_received
  var [lineQtys, setLineQtys] = useState(function() {
    var init = {};
    (po.lines || []).forEach(function(line, i) {
      if (!line.received) {
        init[i] = String(line.qty_ordered || "");
      }
    });
    return init;
  });
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);

  var inp = "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none w-24 text-right";

  var handleSubmit = async function(e) {
    e.preventDefault();
    var linesToReceive = Object.keys(lineQtys)
      .filter(function(i) { return parseFloat(lineQtys[i]) > 0; })
      .map(function(i) {
        return {
          line_index: parseInt(i),
          inventory_item_id: po.lines[i].inventory_item_id,
          qty_received: parseFloat(lineQtys[i])
        };
      });
    if (linesToReceive.length === 0) { setError("Enter a quantity received for at least one line."); return; }
    setSaving(true);
    setError(null);
    try {
      var res = await apiFetch("/api/inventory/purchase-orders/" + po.id + "/receive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines: linesToReceive })
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Receive failed");
      onReceived(data);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  var pendingLines = (po.lines || []).filter(function(l) { return !l.received; });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-green-600" />
            Receive Goods — {po.po_number}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-auto flex-1 px-6 py-4 space-y-2">
            {pendingLines.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">All lines already received.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <tr>
                    <th className="pb-2 text-left">Item</th>
                    <th className="pb-2 text-right w-28">Qty Ordered</th>
                    <th className="pb-2 text-right w-32">Qty to Receive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(po.lines || []).map(function(line, i) {
                    if (line.received) {
                      return (
                        <tr key={i} className="opacity-50">
                          <td className="py-2 pr-3">
                            <div className="font-medium text-gray-600">{line.inventory_item_description}</div>
                            {line.inventory_item_size && <div className="text-xs text-gray-400">{line.inventory_item_size}</div>}
                            <div className="text-xs text-gray-400">{line.supplier_name}</div>
                          </td>
                          <td className="py-2 text-right text-gray-500">{line.qty_ordered}</td>
                          <td className="py-2 text-right">
                            <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                              <CheckCircle className="h-3.5 w-3.5" /> Received
                            </span>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={i}>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-gray-900">{line.inventory_item_description}</div>
                          {line.inventory_item_size && <div className="text-xs text-gray-400">{line.inventory_item_size}</div>}
                          <div className="text-xs text-gray-400">{line.supplier_name}
                            {line.supplier_code ? " · " + line.supplier_code : ""}
                          </div>
                        </td>
                        <td className="py-2 text-right text-gray-600">{line.qty_ordered}</td>
                        <td className="py-2 text-right">
                          <input
                            type="number" min="0" step="any"
                            value={lineQtys[i] ?? ""}
                            onChange={function(e) {
                              var v = e.target.value;
                              setLineQtys(function(prev) { return Object.assign({}, prev, { [i]: v }); });
                            }}
                            className={inp}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {error && (
            <div className="mx-6 mb-2 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            {pendingLines.length > 0 && (
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
                Confirm Receipt
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Add non-stock line inline (shown inside draft PODetail) ──────────────────

const AddLineInline = ({ poId, supplierName, onAdded }) => {
  var blank = { description: "", inventory_item_id: "", supplier_code: "", nominal_code: "", qty_ordered: "1", unit_price: "" };
  var [open, setOpen] = useState(false);
  var [form, setForm] = useState(blank);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);

  var inp = "border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none w-full";
  var setF = function(k, v) { setForm(function(p) { return Object.assign({}, p, { [k]: v }); }); };

  var handleAdd = async function() {
    if (!form.description.trim()) { setError("Description required."); return; }
    setSaving(true); setError(null);
    try {
      var line = {
        inventory_item_id: form.inventory_item_id || "",
        inventory_item_description: form.description.trim(),
        supplier_code: form.supplier_code.trim(),
        nominal_code: form.nominal_code.trim(),
        qty_ordered: parseFloat(form.qty_ordered) || 1,
        unit_price: form.unit_price.trim(),
        supplier_name: supplierName,
        qty_received: 0,
        received: false,
        received_at: ""
      };
      var res = await apiFetch("/api/inventory/purchase-orders/" + poId + "/add-line", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(line)
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      onAdded(data);
      setForm(blank);
      setOpen(false);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={function() { setOpen(true); }}
        className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg px-2 py-1.5">
        <Plus className="h-3.5 w-3.5" /> Add non-stock line
      </button>
    );
  }

  return (
    <div className="bg-purple-50/40 border border-purple-100 rounded-xl p-3 space-y-2">
      <p className="text-xs font-medium text-purple-700">Add non-stock line</p>
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-4">
          <InventorySearchInput
            value={form.description}
            onChange={function(v) { setF("description", v); }}
            onSelect={function(item) {
              var defSup = (item.suppliers || []).find(function(s) { return s.is_default; }) || (item.suppliers || [])[0] || {};
              setForm(function(p) { return Object.assign({}, p, {
                description: item.description || "",
                inventory_item_id: item.id || "",
                supplier_code: defSup.supplier_code || p.supplier_code,
                nominal_code: item.nominal_code || p.nominal_code,
              }); });
            }}
            inputClass={inp}
            placeholder="Search inventory or type description…"
          />
        </div>
        <div className="col-span-2">
          <input value={form.supplier_code} onChange={function(e) { setF("supplier_code", e.target.value); }}
            className={inp + " font-mono"} placeholder="Supplier code" />
        </div>
        <div className="col-span-2">
          <input value={form.nominal_code} onChange={function(e) { setF("nominal_code", e.target.value); }}
            className={inp} placeholder="Nominal code" />
        </div>
        <div className="col-span-1">
          <input type="number" min="0" step="any" value={form.qty_ordered}
            onChange={function(e) { setF("qty_ordered", e.target.value); }}
            className={inp + " text-right"} />
        </div>
        <div className="col-span-2">
          <input type="number" min="0" step="0.0001" value={form.unit_price}
            onChange={function(e) { setF("unit_price", e.target.value); }}
            className={inp + " text-right"} placeholder="0.00" />
        </div>
        <div className="col-span-1 flex justify-end gap-1">
          <button onClick={handleAdd} disabled={saving}
            className="p-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg">
            {saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          </button>
          <button onClick={function() { setOpen(false); setForm(blank); setError(null); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />{error}
        </p>
      )}
      <div className="grid grid-cols-12 gap-2 px-0">
        <div className="col-span-4 text-xs text-gray-400">Description *</div>
        <div className="col-span-2 text-xs text-gray-400">Supplier Code</div>
        <div className="col-span-2 text-xs text-gray-400">Nominal Code</div>
        <div className="col-span-1 text-xs text-gray-400">Qty</div>
        <div className="col-span-2 text-xs text-gray-400">Unit Price</div>
      </div>
    </div>
  );
};

// ── PO expanded detail ────────────────────────────────────────────────────────

const PODetail = ({ po: initialPo, role, onStatusChange, onReceive, onCancel, onDelete, onCreateDN }) => {
  var [po, setPo] = useState(initialPo);
  var [notes, setNotes] = useState(initialPo.notes || "");
  var [editingNotes, setEditingNotes] = useState(false);
  var [savingNotes, setSavingNotes] = useState(false);
  var [hub, setHub] = useState({ id: initialPo.hub_id || "", name: initialPo.hub_name || "" });
  var [editingHub, setEditingHub] = useState(false);
  var [savingHub, setSavingHub] = useState(false);
  var [hubs, setHubs] = useState([]);
  // lineEdits: { [index]: { qty_ordered, unit_price } }
  var [lineEdits, setLineEdits] = useState({});
  var [savingLine, setSavingLine] = useState({});
  var [deletingLine, setDeletingLine] = useState({});
  var [linkedInvoices, setLinkedInvoices] = useState(null); // null=loading, []=none

  // Sync local po state whenever the parent updates the PO (e.g. after a status change)
  useEffect(function() {
    setPo(initialPo);
    setNotes(initialPo.notes || "");
    setHub({ id: initialPo.hub_id || "", name: initialPo.hub_name || "" });
  }, [initialPo]);

  // Fetch hubs for hub picker
  useEffect(function() {
    apiFetch("/api/purchasing/hubs")
      .then(function(r) { return r.json(); })
      .then(function(d) { setHubs(d.items || []); })
      .catch(function() {});
  }, []);

  // Fetch invoices linked to this PO
  useEffect(function() {
    if (!initialPo.id) return;
    apiFetch("/api/purchasing/invoices?limit=100")
      .then(function(r) { return r.ok ? r.json() : { items: [] }; })
      .then(function(d) {
        var linked = (d.items || []).filter(function(inv) {
          return inv.purchase_order_id === initialPo.id;
        });
        setLinkedInvoices(linked);
      })
      .catch(function() { setLinkedInvoices([]); });
  }, [initialPo.id]);

  var isController = role === "po_controller";
  var isDraft = po.status === "draft";
  var isSubmitted = po.status === "submitted";
  var isApproved = po.status === "approved";
  var isSent = po.status === "sent";
  var isActive = !["received", "cancelled"].includes(po.status);

  var saveNotes = async function() {
    setSavingNotes(true);
    try {
      await apiFetch("/api/inventory/purchase-orders/" + po.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: notes })
      });
      setEditingNotes(false);
    } finally {
      setSavingNotes(false);
    }
  };

  var saveHub = async function(newHubId, newHubName) {
    setSavingHub(true);
    try {
      var res = await apiFetch("/api/inventory/purchase-orders/" + po.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hub_id: newHubId, hub_name: newHubName })
      });
      var data = await res.json();
      if (res.ok) { setPo(data); setHub({ id: newHubId, name: newHubName }); }
    } finally {
      setSavingHub(false);
      setEditingHub(false);
    }
  };

  var getLineVal = function(i, field) {
    return (lineEdits[i] && lineEdits[i][field] !== undefined)
      ? lineEdits[i][field]
      : String(po.lines[i][field] ?? "");
  };

  var setLineVal = function(i, field, val) {
    setLineEdits(function(prev) {
      var updated = Object.assign({}, prev);
      updated[i] = Object.assign({}, updated[i] || {}, { [field]: val });
      return updated;
    });
  };

  var hasLineEdits = function(i) {
    if (!lineEdits[i]) return false;
    var e = lineEdits[i];
    var line = po.lines[i];
    return (e.qty_ordered !== undefined && String(e.qty_ordered) !== String(line.qty_ordered ?? ""))
        || (e.unit_price !== undefined && String(e.unit_price) !== String(line.unit_price ?? ""))
        || (e.nominal_code !== undefined && String(e.nominal_code) !== String(line.nominal_code ?? ""));
  };

  var saveLine = async function(i) {
    if (!hasLineEdits(i)) return;
    setSavingLine(function(p) { return Object.assign({}, p, { [i]: true }); });
    try {
      var body = {};
      var e = lineEdits[i] || {};
      if (e.qty_ordered !== undefined) body.qty_ordered = parseFloat(e.qty_ordered) || 0;
      if (e.unit_price !== undefined) body.unit_price = e.unit_price;
      if (e.nominal_code !== undefined) body.nominal_code = e.nominal_code;
      var res = await apiFetch(
        "/api/inventory/purchase-orders/" + po.id + "/lines/" + i,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      );
      var data = await res.json();
      if (res.ok) {
        setPo(data);
        setLineEdits(function(prev) { var n = Object.assign({}, prev); delete n[i]; return n; });
      }
    } finally {
      setSavingLine(function(p) { var n = Object.assign({}, p); delete n[i]; return n; });
    }
  };

  var deleteLine = async function(i) {
    var label = po.lines[i].inventory_item_description || "this line";
    if (!window.confirm("Remove \"" + label + "\" from this PO?")) return;
    setDeletingLine(function(p) { return Object.assign({}, p, { [i]: true }); });
    try {
      var res = await apiFetch(
        "/api/inventory/purchase-orders/" + po.id + "/lines/" + i,
        { method: "DELETE" }
      );
      var data = await res.json();
      if (res.ok) {
        setPo(data);
        // Shift line edits down after deletion
        setLineEdits(function(prev) {
          var n = {};
          Object.keys(prev).forEach(function(k) {
            var ki = parseInt(k);
            if (ki < i) n[ki] = prev[k];
            else if (ki > i) n[ki - 1] = prev[k];
          });
          return n;
        });
      }
    } finally {
      setDeletingLine(function(p) { var n = Object.assign({}, p); delete n[i]; return n; });
    }
  };

  return (
    <div className="bg-gray-50 border-t border-gray-100 px-5 py-4 space-y-3">

      {/* Notes row */}
      <div className="flex items-start gap-2">
        {editingNotes ? (
          <div className="flex items-center gap-2 flex-1">
            <input value={notes} onChange={function(e) { setNotes(e.target.value); }}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="PO notes…" autoFocus />
            <button onClick={saveNotes} disabled={savingNotes}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60">
              {savingNotes ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
            <button onClick={function() { setEditingNotes(false); setNotes(po.notes || ""); }}
              className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            {notes
              ? <p className="text-sm text-gray-600"><span className="font-medium">Notes:</span> {notes}</p>
              : <p className="text-xs text-gray-400 italic">No notes</p>}
            {isActive && (
              <button onClick={function() { setEditingNotes(true); }}
                className="text-xs text-gray-400 hover:text-purple-600 flex items-center gap-1">
                <FileEdit className="h-3 w-3" /> Edit
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hub row */}
      <div className="flex items-center gap-2">
        {editingHub ? (
          <div className="flex items-center gap-2 flex-1">
            <select
              value={hub.id}
              onChange={function(e) {
                var sel = hubs.find(function(h) { return h.id === e.target.value; }) || { id: "", name: "" };
                saveHub(sel.id, sel.name);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white">
              <option value="">— No hub —</option>
              {hubs.map(function(h) { return <option key={h.id} value={h.id}>{h.name}</option>; })}
            </select>
            {savingHub && <RefreshCw className="h-3.5 w-3.5 animate-spin text-purple-500" />}
            <button onClick={function() { setEditingHub(false); }} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            {hub.name
              ? <p className="text-sm text-gray-700 flex items-center gap-1.5"><Building className="h-3.5 w-3.5 text-gray-400" /><span className="font-medium">Hub:</span> {hub.name}</p>
              : <p className="text-xs text-gray-400 italic flex items-center gap-1.5"><Building className="h-3.5 w-3.5" /> No hub assigned</p>}
            {isActive && (
              <button onClick={function() { setEditingHub(true); }}
                className="text-xs text-gray-400 hover:text-purple-600 flex items-center gap-1">
                <FileEdit className="h-3 w-3" /> {hub.name ? "Change" : "Set Hub"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add line button (editable on any pre-received PO) */}
      {isActive && <AddLineInline poId={po.id} supplierName={po.supplier_name || ""} onAdded={function(updated) { setPo(updated); }} />}

      {/* Lines table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr className="text-gray-400 font-medium">
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left w-24">Supplier Code</th>
              <th className="px-3 py-2 text-left w-28">Nominal Code</th>
              <th className="px-3 py-2 text-right w-28">Qty Ordered</th>
              <th className="px-3 py-2 text-right w-28">Unit Price</th>
              <th className="px-3 py-2 text-right w-20">Qty Rcvd</th>
              <th className="px-3 py-2 text-center w-24">Status</th>
              <th className="px-3 py-2 text-left w-36">Received At</th>
              {isActive && <th className="px-3 py-2 w-20"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(po.lines || []).map(function(line, i) {
              var editing = hasLineEdits(i);
              return (
                <tr key={i} className={"hover:bg-gray-50/70" + (line.received ? " opacity-60" : "")}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{line.inventory_item_description}</div>
                    {line.inventory_item_size && <div className="text-gray-400">{line.inventory_item_size}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-500">{line.supplier_code || "—"}</td>
                  <td className="px-3 py-2">
                    {isActive && !line.received ? (
                      <input
                        type="text"
                        value={getLineVal(i, "nominal_code")}
                        onChange={function(e) { setLineVal(i, "nominal_code", e.target.value); }}
                        placeholder="e.g. 5000"
                        className={"w-24 border rounded px-2 py-0.5 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none " +
                          (hasLineEdits(i) ? "border-purple-300 bg-purple-50" : "border-gray-200")}
                      />
                    ) : (
                      <span className="font-mono text-gray-600 text-xs">{line.nominal_code || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isActive && !line.received ? (
                      <input
                        type="number" min="0" step="any"
                        value={getLineVal(i, "qty_ordered")}
                        onChange={function(e) { setLineVal(i, "qty_ordered", e.target.value); }}
                        className={"w-20 text-right border rounded px-2 py-0.5 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none " +
                          (editing ? "border-purple-300 bg-purple-50" : "border-gray-200")}
                      />
                    ) : (
                      <span className="text-gray-700">{line.qty_ordered}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isActive && !line.received ? (
                      <input
                        type="number" min="0" step="0.0001"
                        value={getLineVal(i, "unit_price")}
                        onChange={function(e) { setLineVal(i, "unit_price", e.target.value); }}
                        className={"w-24 text-right border rounded px-2 py-0.5 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none " +
                          (editing ? "border-purple-300 bg-purple-50" : "border-gray-200")}
                        placeholder="0.00"
                      />
                    ) : (
                      <span className="text-gray-600">{line.unit_price ? "£" + line.unit_price : "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-800">{line.qty_received || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {line.received
                      ? <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium"><CheckCircle className="h-3 w-3" />Received</span>
                      : <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium"><Clock className="h-3 w-3" />Pending</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-400">{fmtDate(line.received_at)}</td>
                  {isActive && (
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        {editing && (
                          <button onClick={function() { saveLine(i); }} disabled={!!savingLine[i]}
                            title="Save changes"
                            className="flex items-center gap-0.5 text-xs px-2 py-0.5 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-60">
                            {savingLine[i] ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          </button>
                        )}
                        {!line.received && (
                          <button onClick={function() { deleteLine(i); }} disabled={!!deletingLine[i]}
                            title="Remove line"
                            className="flex items-center gap-0.5 text-xs px-2 py-0.5 border border-gray-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-gray-400 rounded disabled:opacity-60">
                            {deletingLine[i] ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Linked invoices */}
      {linkedInvoices && linkedInvoices.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 space-y-1.5">
          <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Linked Invoice{linkedInvoices.length !== 1 ? "s" : ""}
          </p>
          {linkedInvoices.map(function(inv) {
            return (
              <div key={inv.id} className="flex items-center justify-between bg-white border border-blue-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-gray-800">{inv.invoice_number || inv.id}</span>
                  <span className="ml-2 text-xs text-gray-500">{inv.supplier}</span>
                  <span className="ml-2 text-xs text-gray-400">{inv.invoice_date || ""}</span>
                  {inv.hub_name && <span className="ml-2 text-xs text-gray-400">· {inv.hub_name}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold text-gray-800">{inv.currency || "£"}{inv.total || ""}</span>
                  {inv.paid
                    ? <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Paid</span>
                    : <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">Unpaid</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {linkedInvoices && linkedInvoices.length === 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          No invoices linked to this PO yet.
        </p>
      )}

      {/* Workflow actions */}
      <div className="flex flex-wrap gap-2 pt-1">

        {/* PO User: submit draft for approval */}
        {isDraft && (
          <button onClick={function() { onStatusChange(po, "submitted"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium">
            <Send className="h-3.5 w-3.5" /> Submit for Approval
          </button>
        )}

        {/* Reverse: submitted → draft */}
        {isSubmitted && (
          <button onClick={function() { onStatusChange(po, "draft"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Revert to Draft
          </button>
        )}

        {/* PO Controller only: approve submitted PO */}
        {isSubmitted && isController && (
          <button onClick={function() { onStatusChange(po, "approved"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
            <ThumbsUp className="h-3.5 w-3.5" /> Approve
          </button>
        )}
        {isSubmitted && !isController && (
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg">
            <Lock className="h-3.5 w-3.5" /> Awaiting controller approval
          </span>
        )}

        {/* Reverse: approved → submitted (controller only) */}
        {isApproved && isController && (
          <button onClick={function() { onStatusChange(po, "submitted"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-lg font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Revert to Submitted
          </button>
        )}

        {/* PO Controller only: send approved PO */}
        {isApproved && isController && (
          <button onClick={function() { onStatusChange(po, "sent"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium">
            <Send className="h-3.5 w-3.5" /> Mark as Sent to Supplier
          </button>
        )}

        {/* Reverse: sent → approved (controller only) */}
        {isSent && isController && (
          <button onClick={function() { onStatusChange(po, "approved"); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Revert to Approved
          </button>
        )}

        {/* Create Delivery Note — available for any active PO */}
        {isActive && (
          <button onClick={function() { onCreateDN && onCreateDN(po); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium">
            <Truck className="h-3.5 w-3.5" /> Create Delivery Note
          </button>
        )}

        {/* Mark received — available once sent (or approved for backwards compat) */}
        {(isSent || isApproved || po.status === "partial" || po.status === "pending") && (
          <button onClick={onReceive}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
            <PackageCheck className="h-3.5 w-3.5" /> Mark as Received
          </button>
        )}

        {/* Cancel — any non-terminal status, controller only for submitted/approved/sent */}
        {isActive && (isDraft || isController) && (
          <button onClick={onCancel}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg font-medium">
            <XCircle className="h-3.5 w-3.5" /> Cancel PO
          </button>
        )}

        {/* Delete draft — only for draft POs */}
        {isDraft && (
          <button onClick={onDelete}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 text-gray-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 rounded-lg font-medium">
            <Trash2 className="h-3.5 w-3.5" /> Delete Draft
          </button>
        )}
      </div>
    </div>
  );
};

// ── Main Purchase Orders page ─────────────────────────────────────────────────

const PurchaseOrders = ({ navigateTo, onCreateDN }) => {
  const [pos, setPos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [receiveModal, setReceiveModal] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  // Role: stored in localStorage so it persists between sessions
  const [role, setRole] = useState(function() {
    return localStorage.getItem("po_role") || "po_user";
  });

  var setAndSaveRole = function(r) {
    setRole(r);
    localStorage.setItem("po_role", r);
  };

  const load = useCallback(async function() {
    setLoading(true);
    setLoadError(null);
    try {
      var params = new URLSearchParams({ skip: 0, limit: 200 });
      if (filterStatus) params.set("status", filterStatus);
      var res = await apiFetch("/api/inventory/purchase-orders?" + params.toString());
      var data = await res.json();
      setPos(data.items || []);
      setTotal(data.total || 0);
    } catch(err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(function() { load(); }, [load]);

  // Client-side search filter
  var displayed = pos.filter(function(po) {
    if (!search) return true;
    var q = search.toLowerCase();
    return (po.po_number || "").toLowerCase().includes(q)
      || (po.supplier_name || "").toLowerCase().includes(q);
  });

  var handleReceived = function(updatedPO) {
    setReceiveModal(null);
    setSuccessMsg("Goods received for " + updatedPO.po_number + " — stock updated.");
    setTimeout(function() { setSuccessMsg(null); }, 5000);
    load();
  };

  var handleStatusChange = async function(po, newStatus) {
    var labels = { submitted: "submit", approved: "approve", sent: "mark as sent", cancelled: "cancel", draft: "revert to draft" };
    if (!window.confirm("Are you sure you want to " + (labels[newStatus] || newStatus) + " " + po.po_number + "?")) return;
    await apiFetch("/api/inventory/purchase-orders/" + po.id, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus })
    });
    setSuccessMsg(po.po_number + " → " + newStatus);
    setTimeout(function() { setSuccessMsg(null); }, 4000);
    // Optimistically update the local pos list so PODetail gets the new status immediately
    setPos(function(prev) {
      return prev.map(function(p) { return p.id === po.id ? Object.assign({}, p, { status: newStatus }) : p; });
    });
    load();
  };

  var handleDelete = async function(po) {
    if (!window.confirm("Delete draft PO " + po.po_number + "? This cannot be undone.")) return;
    var res = await apiFetch("/api/inventory/purchase-orders/" + po.id, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      setPos(function(prev) { return prev.filter(function(p) { return p.id !== po.id; }); });
      setExpanded(null);
    }
  };

  var statusCounts = pos.reduce(function(acc, po) {
    acc[po.status] = (acc[po.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {navigateTo && (
              <button onClick={function() { navigateTo("inventory"); }}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-500">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-600 rounded-xl">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
                <p className="text-sm text-gray-500">{total} order{total !== 1 ? "s" : ""}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={function() { setCreateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium">
              <Plus className="h-4 w-4" /> New PO
            </button>
            {/* Role selector */}
            <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Your role:</span>
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              <button onClick={function() { setAndSaveRole("po_user"); }}
                className={"flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                  (role === "po_user" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
                <ShoppingCart className="h-3.5 w-3.5" /> PO User
              </button>
              <button onClick={function() { setAndSaveRole("po_controller"); }}
                className={"flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                  (role === "po_controller" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
                <ShieldCheck className="h-3.5 w-3.5" /> PO Controller
              </button>
            </div>
          </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["draft", "submitted", "approved", "sent"].map(function(s) {
            var meta = STATUS_META[s];
            var count = statusCounts[s] || 0;
            return (
              <div key={s}
                className={"bg-white rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-colors " +
                  (filterStatus === s ? "border-purple-400 ring-1 ring-purple-300" : "border-gray-200 hover:border-gray-300")}
                onClick={function() { setFilterStatus(filterStatus === s ? "" : s); }}>
                <div className={"p-2 rounded-lg " + meta.cls}>
                  {React.cloneElement(meta.icon, { className: "h-5 w-5" })}
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-500 capitalize">{meta.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Success */}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" />{successMsg}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[160px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={function(e) { setSearch(e.target.value); }}
              placeholder="Search PO number or supplier…"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
          </div>
          <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-500">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={load}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" title="Refresh">
            <RefreshCw className={"h-4 w-4 text-gray-500" + (loading ? " animate-spin" : "")} />
          </button>
        </div>

        {/* Error */}
        {loadError && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />{loadError}
          </div>
        )}

        {/* Create PO modal */}
        {createModal && (
          <CreatePOModal
            onClose={function() { setCreateModal(false); }}
            onCreated={function(newPo) {
              setCreateModal(false);
              setSuccessMsg("Created " + newPo.po_number);
              setTimeout(function() { setSuccessMsg(null); }, 4000);
              load();
              setExpanded(newPo.id);
            }}
          />
        )}

        {/* Receive modal */}
        {receiveModal && (
          <ReceiveModal
            po={receiveModal}
            onClose={function() { setReceiveModal(null); }}
            onReceived={handleReceived}
          />
        )}

        {/* List */}
        {displayed.length === 0 && !loading && !loadError ? (
          <div className="bg-white rounded-xl border border-gray-200 p-14 text-center text-gray-400">
            <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No purchase orders found</p>
            <p className="text-sm mt-1">Create a new PO directly or add items from Inventory</p>
            <div className="flex items-center justify-center gap-3 mt-4">
              <button onClick={function() { setCreateModal(true); }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2">
                <Plus className="h-4 w-4" /> New PO
              </button>
              {navigateTo && (
                <button onClick={function() { navigateTo("inventory"); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
                  Go to Inventory
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  <th className="px-4 py-3 text-left w-6"></th>
                  <th className="px-4 py-3 text-left">PO Number</th>
                  <th className="px-4 py-3 text-left">Supplier</th>
                  <th className="px-4 py-3 text-left w-32">Hub</th>
                  <th className="px-4 py-3 text-center w-20">Lines</th>
                  <th className="px-4 py-3 text-center w-28">Status</th>
                  <th className="px-4 py-3 text-left w-36">Raised</th>
                  <th className="px-4 py-3 text-left w-36">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayed.map(function(po) {
                  var isOpen = expanded === po.id;
                  var meta = STATUS_META[po.status] || STATUS_META["pending"];
                  var receivedLines = (po.lines || []).filter(function(l) { return l.received; }).length;
                  return (
                    <React.Fragment key={po.id}>
                      <tr
                        className={"cursor-pointer hover:bg-purple-50/30 transition-colors" + (isOpen ? " bg-purple-50/20" : "")}
                        onClick={function() { setExpanded(expanded === po.id ? null : po.id); }}>
                        <td className="px-4 py-3 text-gray-400">
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-mono font-medium text-gray-900">{po.po_number}</div>
                          {po.notes && <div className="text-xs text-gray-400 truncate max-w-xs">{po.notes}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{po.supplier_name || "—"}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{po.hub_name || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs text-gray-600">
                            {receivedLines}/{(po.lines || []).length}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={"inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium " + meta.cls}>
                            {meta.icon}{meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(po.created_at)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(po.updated_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <PODetail
                              po={po}
                              role={role}
                              onStatusChange={handleStatusChange}
                              onReceive={function() { setReceiveModal(po); }}
                              onCancel={function() { handleStatusChange(po, "cancelled"); }}
                              onDelete={function() { handleDelete(po); }}
                              onCreateDN={onCreateDN}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrders;
